import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { Info } from '@mui/icons-material';
import { ClickAwayListener, Grow, Paper, Popper } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import type { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect } from 'react';
import { parseViewFieldValues } from 'src/api/viewModels/data';
import { buildViewFields } from 'src/api/viewModels/queryWrappers';
import viewStateVar from 'src/store/viewState';

const useStyles = makeStyles((theme: Theme) => ({
  cardInfoIcon: {
    zIndex: 1,
    position: 'absolute',
    right: '10px',
    top: '45px',
    cursor: 'pointer',
    '& svg': {
      color: theme.palette.text.primary,
      padding: '2px',
      backgroundColor: theme.palette.background.default,
      borderRadius: '4px',
    },
  },
  cardInfoBox: {
    position: 'relative',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    padding: '10px',
    borderRadius: '4px',
    width: '200px',
    wordBreak: 'break-all',
    zIndex: 1001,
  },
  popper: {
    zIndex: 1000,
  },
  button: {
    minWidth: 'auto',
  },
  popupTitle: {
    color: theme.palette.text.disabled,
  },
}));

interface MediaCardInfoIconProps {
  itemId: string;
}

interface ViewFieldDisplay {
  fieldLabel?: string;
  show?: string | null;
}

const MediaCardInfoIcon: React.FC<MediaCardInfoIconProps> = ({ itemId }) => {
  const classes = useStyles();
  const [open, setOpen] = React.useState<boolean>(false);
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const viewState = useReactiveVar(viewStateVar);

  const { ViewMediaCardInfo } = viewState.viewModels.library;
  const [getViewFields, { data, loading, error }] = useLazyQuery(buildViewFields(), {
    variables: { globalId: itemId, view_ComponentName: ViewMediaCardInfo?.node?.componentName },
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'cache-only',
  });

  useEffect(() => {
    if (open) {
      getViewFields();
    }
  }, [open, getViewFields]);

  const handleToggle = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent): void => {
    if (anchorRef.current?.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  if (error) return <>{`Error! ${error.message}`}</>;

  const viewFields = (parseViewFieldValues(data) ?? []) as ViewFieldDisplay[];

  return (
    <div className={classes.cardInfoIcon} ref={anchorRef}>
      <Button
        variant="text"
        color="primary"
        size="small"
        aria-controls={open ? 'split-button-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-label="select merge strategy"
        aria-haspopup="menu"
        classes={{ root: classes.button }}
        onClick={handleToggle}
      >
        <Info />
      </Button>
      <Popper open={open} anchorEl={anchorRef.current} transition placement="left-start" className={classes.popper}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              zIndex: 1050,
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                {loading ? (
                  <CircularProgress color="secondary" />
                ) : (
                  <Box className={classes.cardInfoBox}>
                    {viewFields?.map((field, key) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: view fields have no stable unique identifier
                      <Box style={{ margin: '10px' }} key={key}>
                        <Typography className={classes.popupTitle}>{field.fieldLabel}:</Typography>
                        <Typography>{field.show}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default MediaCardInfoIcon;
